import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getConsent, setConsent } from '../lib/analytics';

/**
 * Bottom-right cookie consent banner.
 *
 * Shows only on first visit (or after "Manage preferences" reset). Accept
 * → GA4 loads and starts sending pageviews. Decline → GA4 never loads;
 * gtag stays in the noop `dataLayer` state configured in index.html.
 *
 * Deliberately NOT a full-width overlay — that pattern annoys visitors,
 * hurts bounce rate, and doesn't measurably improve consent rates.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only show if the user has neither accepted nor declined before.
    // A short delay lets the page settle first so the banner isn't jarring
    // during initial paint.
    const t = setTimeout(() => {
      if (getConsent() === null) setVisible(true);
    }, 900);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const decide = (value: 'accepted' | 'declined') => {
    setConsent(value);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        left: 20,
        maxWidth: 420,
        marginLeft: 'auto',
        zIndex: 998, // below toast (999) so error toasts still show on top
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 14,
        boxShadow: '0 20px 48px rgba(30, 37, 71, 0.18)',
        padding: 20,
        fontSize: 14,
        lineHeight: 1.5,
        color: '#262F59',
        fontFamily: 'Poppins, Arial, sans-serif',
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8, color: '#1E2547' }}>
        Cookies &amp; analytics
      </div>
      <p style={{ margin: '0 0 14px', color: '#4a5568' }}>
        We&apos;d like to use Google Analytics to understand which pages help visitors most.
        No ads, no personal data — just anonymous page views. You can decline and use the site normally.
        See our <Link to="/privacy" style={{ color: '#395ACB', textDecoration: 'underline' }}>Privacy Policy</Link>.
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => decide('declined')}
          style={{
            background: 'transparent',
            border: '1px solid #cbd5e0',
            color: '#4a5568',
            padding: '8px 16px',
            borderRadius: 50,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Decline
        </button>
        <button
          type="button"
          onClick={() => decide('accepted')}
          style={{
            background: '#0F9E45',
            border: 'none',
            color: '#fff',
            padding: '8px 18px',
            borderRadius: 50,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Accept
        </button>
      </div>
    </div>
  );
}
